<script setup lang="ts">
import { computed } from "vue"
import AppShell from "../Layout/AppShell.vue"
import DashboardWorkloadChart from "./components/DashboardWorkloadChart.vue"
import { formatDate } from "@/utils/date"

type DashboardPayload = {
  generated_at: string
  kpis: {
    overdue_sales_orders: number
    overdue_production_orders: number
    overdue_purchases: number
    incoming_purchases: number
    low_stock_products: number
  }
  chart: {
    workload_status: Array<{ name: string; value: number }>
  }
  lists: {
    overdue_sales_orders: Array<{ id: number; company: string; customer: string; delivery_date: string | null; status: string }>
    overdue_production_orders: Array<{ id: number; company: string; expected_at: string | null; status: string; sales_order_id: number | null }>
    overdue_purchases: Array<{ id: number; company: string; supplier: string; expected_at: string | null; status: string }>
    incoming_purchases: Array<{ id: number; company: string; supplier: string; expected_at: string | null; status: string }>
    low_stock_products: Array<{ id: number; company: string; sku: string; name: string; unit: string; stock_balance: string }>
  }
}

const props = defineProps<{ dashboard: DashboardPayload }>()

const purchasesTotal = props.dashboard.kpis.overdue_purchases + props.dashboard.kpis.incoming_purchases

const purchaseAlerts = computed(() => {
  const overdue = props.dashboard.lists.overdue_purchases.map((order) => ({ ...order, urgency: "overdue" as const }))
  const onTime = props.dashboard.lists.incoming_purchases.map((order) => ({ ...order, urgency: "on_time" as const }))

  return [...overdue, ...onTime].sort((a, b) => {
    if (a.urgency !== b.urgency) return a.urgency === "overdue" ? -1 : 1
    const dateA = a.expected_at ? new Date(a.expected_at).getTime() : Number.MAX_SAFE_INTEGER
    const dateB = b.expected_at ? new Date(b.expected_at).getTime() : Number.MAX_SAFE_INTEGER
    return dateA - dateB
  })
})

const kpiCards = [
  { label: "Pedidos de venda em atraso", value: props.dashboard.kpis.overdue_sales_orders, tone: "text-rose-700 bg-rose-50 border-rose-200" },
  { label: "Ordens de producao em atraso", value: props.dashboard.kpis.overdue_production_orders, tone: "text-amber-700 bg-amber-50 border-amber-200" },
  { label: "Compras monitoradas", value: purchasesTotal, tone: "text-sky-700 bg-sky-50 border-sky-200" },
  { label: "Estoque baixo", value: props.dashboard.kpis.low_stock_products, tone: "text-violet-700 bg-violet-50 border-violet-200" },
]

function formatStatus(raw: string): string {
  return {
    draft: "Rascunho",
    approved: "Aprovado",
    in_production: "Em producao",
    pending: "Pendente",
    in_progress: "Em andamento",
    ordered: "Pedido enviado",
  }[raw] || raw
}

function formatStock(balance: string): string {
  return Number(balance).toFixed(3).replace(".", ",")
}
</script>

<template>
  <AppShell>
    <div class="space-y-6">
      <section class="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h1 class="text-2xl font-semibold text-slate-900">Dashboard operacional</h1>
        <p class="mt-2 text-sm text-slate-600">Acompanhe os principais riscos operacionais e prioridades do dia.</p>
      </section>

      <section class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <article
          v-for="card in kpiCards"
          :key="card.label"
          class="rounded-lg border p-4 shadow-sm"
          :class="card.tone"
        >
          <p class="text-sm font-medium">{{ card.label }}</p>
          <p class="mt-2 text-3xl font-semibold">{{ card.value }}</p>
        </article>
      </section>

      <section class="grid gap-6 xl:grid-cols-2">
        <article class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <h2 class="text-sm font-semibold text-slate-800">Visao consolidada</h2>
          <DashboardWorkloadChart :points="dashboard.chart.workload_status" />
        </article>

        <article class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <h2 class="mb-3 text-sm font-semibold text-slate-800">Pedidos de venda em atraso</h2>
          <div class="space-y-2">
            <p v-if="dashboard.lists.overdue_sales_orders.length === 0" class="text-sm text-slate-500">Nenhum pedido de venda em atraso.</p>
            <div v-for="order in dashboard.lists.overdue_sales_orders" :key="order.id" class="rounded-md border border-slate-100 p-3">
              <p class="text-sm font-medium text-slate-900">Pedido #{{ order.id }} - {{ order.customer }}</p>
              <p class="text-xs text-slate-600">Empresa: {{ order.company }}</p>
              <p class="text-xs text-slate-600">Entrega: {{ formatDate(order.delivery_date) }}</p>
              <p class="text-xs text-slate-600">Status: {{ formatStatus(order.status) }}</p>
            </div>
          </div>
        </article>
      </section>

      <section class="grid gap-6 xl:grid-cols-3">
        <article class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <h2 class="mb-3 text-sm font-semibold text-slate-800">Compras monitoradas</h2>
          <div class="space-y-2">
            <p v-if="purchaseAlerts.length === 0" class="text-sm text-slate-500">Nenhuma compra monitorada.</p>
            <div v-for="order in purchaseAlerts" :key="`${order.urgency}-${order.id}`" class="rounded-md border border-slate-100 p-3">
              <p class="text-sm font-medium text-slate-900">Compra #{{ order.id }} - {{ order.supplier }}</p>
              <p class="text-xs text-slate-600">Empresa: {{ order.company }}</p>
              <p class="text-xs text-slate-600">Entrega prevista: {{ formatDate(order.expected_at) }}</p>
              <p class="text-xs text-slate-600">Status: {{ formatStatus(order.status) }}</p>
              <span
                class="mt-2 inline-flex rounded-full px-2 py-0.5 text-xs font-medium"
                :class="order.urgency === 'overdue' ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'"
              >
                {{ order.urgency === "overdue" ? "Atrasado" : "Dentro do prazo" }}
              </span>
            </div>
          </div>
        </article>

        <article class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <h2 class="mb-3 text-sm font-semibold text-slate-800">Ordens de producao em atraso</h2>
          <div class="space-y-2">
            <p v-if="dashboard.lists.overdue_production_orders.length === 0" class="text-sm text-slate-500">Nenhuma ordem de producao em atraso.</p>
            <div v-for="order in dashboard.lists.overdue_production_orders" :key="order.id" class="rounded-md border border-slate-100 p-3">
              <p class="text-sm font-medium text-slate-900">OP #{{ order.id }}</p>
              <p class="text-xs text-slate-600">Empresa: {{ order.company }}</p>
              <p class="text-xs text-slate-600">Prevista para: {{ formatDate(order.expected_at) }}</p>
              <p class="text-xs text-slate-600">Status: {{ formatStatus(order.status) }}</p>
            </div>
          </div>
        </article>

        <article class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <h2 class="mb-3 text-sm font-semibold text-slate-800">Estoques baixos</h2>
          <div class="space-y-2">
            <p v-if="dashboard.lists.low_stock_products.length === 0" class="text-sm text-slate-500">Nenhum produto em nivel critico.</p>
            <div v-for="product in dashboard.lists.low_stock_products" :key="product.id" class="rounded-md border border-slate-100 p-3">
              <p class="text-sm font-medium text-slate-900">{{ product.sku }} - {{ product.name }}</p>
              <p class="text-xs text-slate-600">Empresa: {{ product.company }}</p>
              <p class="text-xs text-slate-600">Saldo: {{ formatStock(product.stock_balance) }} {{ product.unit }}</p>
            </div>
          </div>
        </article>
      </section>
    </div>
  </AppShell>
</template>


